import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDevice } from '../../../hooks/useDevice';
import axios from 'axios';

/**
 * Hook to manage unified onboarding logic (Mobile & Web).
 * SOLID: Responsibility is orchestrating onboarding state and rules.
 * Refactored: strictly Account-based visibility via backend flag.
 */
export const useOnboarding = () => {
  const { user } = useAuth();
  const { isMobile } = useDevice();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Show the welcome modal only once for newly created users.
    // Existing users without the flag (null/undefined) should not see it.
    if (user && user.has_seen_welcome_modal === false) {
      const timer = setTimeout(async () => {
        console.log('📦 Showing welcome modal for the first time:', user.email);

        setShowModal(true);

        try {
          // Mark it as seen as soon as it is displayed.
          // This prevents it from appearing again even if WABA is not configured.
          await axios.put('/auth/me/welcome-modal');
          console.log('📦 Welcome modal marked as seen');
        } catch (e) {
          console.error('Failed to update welcome modal status:', e);
        }
      }, 1500);

      return () => clearTimeout(timer);
    }

    console.log('📦 Welcome modal conditions not met:', {
      hasUser: !!user,
      hasSeenWelcomeModal: user?.has_seen_welcome_modal
    });
  }, [user]);

  const handleDismiss = useCallback(async () => {
    setShowModal(false);
    
    // If on mobile, we mark it as seen immediately since there is no desktop tour
    if (isMobile) {
      try {
        await axios.put('/auth/me/onboarding-tour');
        console.log('📦 Onboarding marked as seen via Mobile (Account-sync)');
      } catch (e) {
        console.error('Failed to update onboarding status on mobile:', e);
      }
    } else {
      // For Web users, we trigger the guided tour right after this modal
      // The GuidedTour component will handle the backend markTourSeen() later
      window.dispatchEvent(new CustomEvent('toggle-guided-tour'));
    }
  }, [isMobile]);

  return {
    showModal,
    mode: (isMobile ? 'mobile' : 'web') as 'mobile' | 'web',
    handleDismiss
  };
};
